import React, { useState, useEffect, useContext } from 'react';
import { Button, Modal, Form, Badge, Spinner, Row, Col, Alert } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import leaveRequestService from '../services/leaveRequestService';
import { leaveTypeService } from '../services/leaveTypeService';
import { AuthContext } from '../context/AuthContext';
import ResponsiveTable from './ResponsiveTable';

const LeaveRequest = () => {
    const [requests, setRequests] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const { user, loading: authLoading } = useContext(AuthContext);
    const [remainingDays, setRemainingDays] = useState(null);
    const [formData, setFormData] = useState({
        leaveTypeId: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        reason: ''
    });
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [documents, setDocuments] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                await Promise.all([
                    fetchLeaveRequests(),
                    fetchLeaveTypes()
                ]);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            loadData();
        }
    }, [user, authLoading]);

    const fetchLeaveTypes = async () => {
        try {
            const data = await leaveTypeService.getActiveLeaveTypes();
            setLeaveTypes(data);
        } catch (error) {
            toast.error('Failed to fetch leave types');
            console.error(error);
        }
    };

    const fetchLeaveRequests = async () => {
        try {
            const data = await leaveRequestService.getUserRequests(user.userId);
            setRequests(data);
        } catch (error) {
            toast.error('Failed to fetch leave requests');
            console.error(error);
        }
    };

    const handleProcess = async (id, action) => {
        try {
            const result = await leaveRequestService.process(id, action);
            if (result) {
                toast.success(`Leave request ${action.toLowerCase()} successfully`);
                fetchLeaveRequests();
            }
        } catch (error) {
            toast.error(`Failed to ${action.toLowerCase()} leave request`);
            console.error(error);
        }
    };

    const handleShowModal = async (request) => {
        setSelectedRequest(request);
        try {
            // Fetch remaining days when modal opens
            const response = await leaveRequestService.getRemainingLeaveDays(user.userId);
            const days = JSON.parse(response); // Parse the response if it's a string
            setRemainingDays(days);
            console.log('Remaining days response:', days); // Debug log
        } catch (error) {
            console.error('Failed to fetch remaining leave days:', error);
            setRemainingDays(null);
        }

        if (request) {
            // Format date as YYYY-MM-DD without timezone
            const formatDate = (dateString) => {
                const date = new Date(dateString);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            };

            // Extract time from datetime for hourly requests
            const extractTime = (dateString) => {
                if (!dateString) return '';
                
                const date = new Date(dateString);
                return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
            };

            const startDate = formatDate(request.startDate);
            const endDate = formatDate(request.endDate);
            
            // Get times from the date objects if they are not explicitly provided
            const leaveType = leaveTypes.find(lt => lt.id === request.leaveTypeId);
            const isHourly = leaveType?.isHourly || false;
            
            let startTime = request.startTime || '';
            let endTime = request.endTime || '';
            
            // If this is an hourly request but times are not stored separately, extract them
            if (isHourly && (!startTime || !endTime)) {
                startTime = extractTime(request.startDate);
                endTime = extractTime(request.endDate);
            }
            
            setFormData({
                leaveTypeId: request.leaveTypeId.toString(),
                startDate: startDate,
                endDate: endDate,
                startTime: startTime,
                endTime: endTime,
                reason: request.reason || ''
            });
        } else {
            // Reset form for new request
            setFormData({
                leaveTypeId: '',
                startDate: '',
                endDate: '',
                startTime: '',
                endTime: '',
                reason: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            leaveTypeId: '',
            startDate: '',
            endDate: '',
            startTime: '',
            endTime: '',
            reason: ''
        });
        setSelectedRequest(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If leave type is changed and it's not hourly, set end date based on start date and default days
        if (name === 'leaveTypeId' && value) {
            const selectedType = leaveTypes.find(lt => lt.id === parseInt(value));
            if (selectedType && !selectedType.isHourly && selectedType.defaultDays > 0) {
                const startDate = formData.startDate || new Date().toISOString().split('T')[0];
                const endDate = new Date(startDate);
                // Subtract 1 from defaultDays since we count inclusively
                endDate.setDate(endDate.getDate() + selectedType.defaultDays - 1);
                
                // Format the date as YYYY-MM-DD
                const formattedEndDate = endDate.toISOString().split('T')[0];
                
                setFormData(prev => ({
                    ...prev,
                    startDate: startDate,
                    endDate: formattedEndDate
                }));
            }
        }

        // Validate dates when either date field changes
        if (name === 'startDate' || name === 'endDate') {
            const start = name === 'startDate' ? value : formData.startDate;
            const end = name === 'endDate' ? value : formData.endDate;
            
            if (start && end && !isSelectedLeaveTypeHourly()) {
                const startDate = new Date(start);
                const endDate = new Date(end);
                
                if (startDate > endDate) {
                    toast.error('Start date cannot be after end date');
                }
            }
        }

        // Validate times when either time field changes
        if (isSelectedLeaveTypeHourly() && (name === 'startTime' || name === 'endTime')) {
            const start = name === 'startTime' ? value : formData.startTime;
            const end = name === 'endTime' ? value : formData.endTime;
            
            if (start && end) {
                const [startHour, startMinute] = start.split(':').map(Number);
                const [endHour, endMinute] = end.split(':').map(Number);
                
                if (startHour > endHour || (startHour === endHour && startMinute >= endMinute)) {
                    toast.error('Start time must be before end time');
                }
            }
        }
    };

    const handleFileChange = (e) => {
        setDocuments(Array.from(e.target.files));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate dates before submission
        if (!isSelectedLeaveTypeHourly() && formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate);
            const endDate = new Date(formData.endDate);
            
            if (startDate > endDate) {
                toast.error('Start date cannot be after end date');
                return;
            }
        }

        // Validate times for hourly requests
        if (isSelectedLeaveTypeHourly() && formData.startTime && formData.endTime) {
            const [startHour, startMinute] = formData.startTime.split(':').map(Number);
            const [endHour, endMinute] = formData.endTime.split(':').map(Number);
            
            if (startHour > endHour || (startHour === endHour && startMinute >= endMinute)) {
                toast.error('Start time must be before end time');
                return;
            }
        }

        // Validate documents if required
        if (isSelectedLeaveTypeRequiresDocumentation() && (!documents || documents.length === 0)) {
            toast.error('Documentation is required for this leave type');
            return;
        }

        try {
            let submissionData = {
                leaveTypeId: parseInt(formData.leaveTypeId),
                reason: formData.reason
            };
            
            if (isSelectedLeaveTypeHourly()) {
                // For hourly leave types, combine date and time with UTC timezone
                const dateStr = formData.startDate; // format: YYYY-MM-DD
                const startDateTime = new Date(`${dateStr}T${formData.startTime}:00Z`);
                const endDateTime = new Date(`${dateStr}T${formData.endTime}:00Z`);
                
                submissionData.startDate = startDateTime.toISOString();
                submissionData.endDate = endDateTime.toISOString();
            } else {
                // For daily leave types, set time to start/end of day in UTC
                const startDate = new Date(formData.startDate);
                startDate.setUTCHours(0, 0, 0, 0);
                const endDate = new Date(formData.endDate);
                endDate.setUTCHours(23, 59, 59, 999);
                
                submissionData.startDate = startDate.toISOString();
                submissionData.endDate = endDate.toISOString();
            }

            // Create FormData for file upload
            const requestFormData = new FormData();
            requestFormData.append('leaveTypeId', submissionData.leaveTypeId);
            requestFormData.append('startDate', submissionData.startDate);
            requestFormData.append('endDate', submissionData.endDate);
            requestFormData.append('reason', submissionData.reason);
            
            // Append each document
            if (documents && documents.length > 0) {
                documents.forEach((file, index) => {
                    requestFormData.append(`documents`, file);
                });
            }

            const result = await leaveRequestService.create(user.userId, requestFormData);
            if (result) {
                toast.success('Leave request submitted successfully');
                handleCloseModal();
                fetchLeaveRequests();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit leave request');
            console.error(error);
        }
    };

    const getStatusBadge = (status) => {
        const statusVariants = {
            'Pending': 'warning',
            'Approved': 'success',
            'Rejected': 'danger',
            'Cancelled': 'dark'
        };
        return <Badge bg={statusVariants[status] || 'secondary'}>{status}</Badge>;
    };

    const getLeaveTypeName = (leaveTypeId) => {
        const leaveType = leaveTypes.find(lt => lt.id === leaveTypeId);
        return leaveType ? leaveType.name : 'Unknown';
    };

    const isSelectedLeaveTypeHourly = () => {
        const selectedType = leaveTypes.find(lt => lt.id === parseInt(formData.leaveTypeId));
        return selectedType?.isHourly || false;
    };

    const isSelectedLeaveTypeRequiresDocumentation = () => {
        const selectedType = leaveTypes.find(lt => lt.id === parseInt(formData.leaveTypeId));
        return selectedType?.requiresDocumentation || false;
    };

    // Define columns for ResponsiveTable
    const columns = [
        {
            key: 'leaveType',
            label: 'Leave Type',
            render: (_, item) => getLeaveTypeName(item.leaveTypeId)
        },
        {
            key: 'createdAt',
            label: 'Created Date/Time',
            render: (value) => {
                if (!value) return '-';
                const date = new Date(value);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
        },
        {
            key: 'startDate',
            label: 'Start',
            render: (value, item) => {
                const date = new Date(value).toLocaleDateString();
                return item.startTime ? `${date} ${item.startTime}` : date;
            }
        },
        {
            key: 'endDate',
            label: 'End',
            render: (value, item) => {
                const date = new Date(value).toLocaleDateString();
                return item.endTime ? `${date} ${item.endTime}` : date;
            }
        },
        {
            key: 'totalDays',
            label: 'Duration',
            render: (value, item) => {
                const leaveType = leaveTypes.find(lt => lt.id === item.leaveTypeId);
                
                if (leaveType?.isHourly) {
                    // Get the start and end dates as Date objects
                    const startDate = new Date(item.startDate);
                    const endDate = new Date(item.endDate);
                    
                    // Calculate the difference in milliseconds
                    const diffMs = endDate.getTime() - startDate.getTime();
                    
                    // Convert to minutes
                    const diffMinutes = Math.round(diffMs / (1000 * 60));
                    
                    // Convert to hours and minutes
                    const hours = Math.floor(diffMinutes / 60);
                    const minutes = diffMinutes % 60;
                    
                    if (minutes === 0) {
                        return `${hours} hour${hours !== 1 ? 's' : ''}`;
                    } else if (hours === 0) {
                        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    } else {
                        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    }
                } else {
                    // Round to nearest whole number for days
                    const days = Math.round(value);
                    return `${days} day${days !== 1 ? 's' : ''}`;
                }
            }
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value)
        }
    ];

    // Define action buttons separately
    const actionButtons = [
        { 
            icon: <BsPencil />, 
            variant: "outline-primary", 
            onClick: (item) => {
                if (item.status === 'Pending') {
                    handleShowModal(item);
                }
            },
            label: "Edit"
        },
        { 
            icon: <BsTrash />, 
            variant: "outline-danger", 
            onClick: (item) => {
                if (item.status === 'Pending') {
                    handleProcess(item.id, 'Cancel');
                }
            },
            label: "Cancel"
        }
    ];

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
    }

    if (!user) {
        return <div className="text-center p-4">Please log in to view leave requests.</div>;
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => handleShowModal(null)}>
                        <BsPlusLg /> Add New Request
                    </Button>
                </div>
            </div>

            <ResponsiveTable 
                data={requests}
                columns={columns}
                actions={actionButtons}
            />

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedRequest ? 'Edit Leave Request' : 'Add New Leave Request'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {remainingDays && (
                            <Alert variant="info" className="mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Annual Leave: </strong>
                                        {typeof remainingDays.AnnualLeave === 'number' ? remainingDays.AnnualLeave : '0'} days remaining
                                    </div>
                                    <div>
                                        <strong>Sick Leave: </strong>
                                        {typeof remainingDays.SickLeave === 'number' ? remainingDays.SickLeave : '0'} days remaining
                                    </div>
                                </div>
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Leave Type</Form.Label>
                            <Form.Select
                                name="leaveTypeId"
                                value={formData.leaveTypeId}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select Leave Type</option>
                                {leaveTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name} {type.defaultDays > 0 && !type.isHourly && `(${type.defaultDays} days)`}
                                        {type.isHourly && ' (Hourly)'}
                                        {type.requiresDocumentation && ' (Requires Documentation)'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            <Col md={isSelectedLeaveTypeHourly() ? 12 : 6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{isSelectedLeaveTypeHourly() ? 'Date' : 'Start Date'}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                                {isSelectedLeaveTypeHourly() && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Start Time</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    name="startTime"
                                                    value={formData.startTime}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>End Time</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    name="endTime"
                                                    value={formData.endTime}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                            {!isSelectedLeaveTypeHourly() && (
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="endDate"
                                            value={formData.endDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {isSelectedLeaveTypeRequiresDocumentation() && (
                            <Form.Group className="mb-3">
                                <Form.Label>Required Documentation</Form.Label>
                                <Form.Control
                                    type="file"
                                    multiple
                                    onChange={handleFileChange}
                                    required={isSelectedLeaveTypeRequiresDocumentation()}
                                />
                                <Form.Text className="text-muted">
                                    Please upload all required documents for this leave type.
                                </Form.Text>
                                {documents.length > 0 && (
                                    <div className="mt-2">
                                        <strong>Selected files:</strong>
                                        <ul className="list-unstyled">
                                            {documents.map((file, index) => (
                                                <li key={index}>{file.name}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name="reason"
                                value={formData.reason}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {selectedRequest ? 'Update' : 'Add'} Leave Request
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default LeaveRequest; 